import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft, Save, Rocket, Eye, EyeOff, Plus, Trash2, Copy, ArrowUp, ArrowDown,
  GripVertical, Layers, Columns as ColumnsIcon, Heading, Type, Image as ImageIcon,
  Images, MousePointerClick, Video, Minus, MoveVertical, ExternalLink, Settings, Monitor,
  Undo2, Redo2, Smartphone, Tablet, Bookmark, History, LayoutTemplate, X, Sparkles, TrendingUp, Puzzle,
} from "lucide-react";
import BlockRenderer from "@/components/cms/BlockRenderer";
import PropertiesPanel from "./PropertiesPanel";
import AiModal from "./AiModal";
import ConversionModal from "./ConversionModal";
import PresetGallery from "./PresetGallery";
import SeoChecklist from "./SeoChecklist";
import {
  BLOCK_LIBRARY, DEVICE_WIDTHS, clone, findBlock, newColumn, newLeaf, newSection, normalizeSections, styleClasses,
} from "./constants";

const ICONS = { Heading, Type, Image: ImageIcon, Images, MousePointerClick, Video, Minus, MoveVertical, Puzzle };
const rid = () => Math.random().toString(36).slice(2, 10);
const reidSection = (s) => ({ ...s, id: rid(), columns: (s.columns || []).map((c) => ({ id: rid(), blocks: (c.blocks || []).map((b) => ({ ...b, id: rid() })) })) });

function SortableLeaf({ block, selected, onSelect, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={`relative group rounded-lg border-2 transition-colors ${selected ? "border-[#E05638]" : "border-transparent hover:border-slate-200"}`}
      data-testid={`canvas-block-${block.type}`}>
      <div className="absolute -top-3 right-2 z-10 hidden group-hover:flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 py-0.5">
        <button {...attributes} {...listeners} className="p-1 cursor-grab touch-none text-slate-500 hover:text-slate-800" title="Déplacer" data-testid={`canvas-drag-${block.id}`}><GripVertical size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="p-1 text-slate-500 hover:text-slate-800" title="Dupliquer"><Copy size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 text-rose-500 hover:text-rose-700" title="Supprimer"><Trash2 size={13} /></button>
      </div>
      <div className="p-2 pointer-events-none"><BlockRenderer block={block} /></div>
    </div>
  );
}

function ColumnDrop({ id, children, empty }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} data-testid={`col-drop-${id}`}
      className={`min-h-[64px] rounded-lg p-2 space-y-2 transition-colors ${isOver ? "bg-orange-50 ring-2 ring-[#E05638]/40" : "bg-slate-50/60"}`}>
      {children}
      {empty && <div className="text-center text-[11px] text-slate-400 py-5 border border-dashed border-slate-200 rounded-md">Colonne vide — déposez un bloc</div>}
    </div>
  );
}

const paddPreview = { none: "", sm: "py-3", md: "py-6", lg: "py-10" };

// Édition d'une section en "position libre" : blocs déplaçables + redimensionnables (largeur).
function FreeSection({ section, selectedId, onSelectBlock, patchPos, onDup, onDel, onInteractStart, onInteractEnd }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const flat = section.columns.flatMap((c) => c.blocks);
  const onDown = (e, b, mode, idx) => {
    e.preventDefault(); e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    const p = (b.data || {}).pos || { x: 5, y: 10 + idx * 90, w: 60 };
    drag.current = { id: b.id, mode, startX: e.clientX, startY: e.clientY, rect, p: { ...p } };
    onInteractStart && onInteractStart();
    const onMove = (ev) => {
      const dc = drag.current; if (!dc) return;
      const dxPct = ((ev.clientX - dc.startX) / dc.rect.width) * 100;
      const dy = ev.clientY - dc.startY;
      if (dc.mode === "move") patchPos(dc.id, { x: Math.max(0, Math.min(95, dc.p.x + dxPct)), y: Math.max(0, dc.p.y + dy), w: dc.p.w });
      else patchPos(dc.id, { x: dc.p.x, y: dc.p.y, w: Math.max(10, Math.min(100, dc.p.w + dxPct)) });
    };
    const onUp = () => { drag.current = null; onInteractEnd && onInteractEnd(); window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove); window.addEventListener("pointerup", onUp);
  };
  return (
    <div ref={ref} className="relative bg-slate-50 rounded-lg overflow-hidden" style={{ height: `${section.data.freeHeight || 420}px` }} data-testid="free-canvas">
      {flat.map((b, idx) => {
        const p = (b.data || {}).pos || { x: 5, y: 10 + idx * 90, w: 60 };
        const sel = selectedId === b.id;
        return (
          <div key={b.id} onPointerDown={(e) => onDown(e, b, "move", idx)} onClick={(e) => { e.stopPropagation(); onSelectBlock(b.id); }}
            className={`absolute group border-2 rounded bg-white ${sel ? "border-[#E05638]" : "border-transparent hover:border-slate-300"}`}
            style={{ left: `${p.x}%`, top: `${p.y}px`, width: `${p.w}%`, cursor: "move" }} data-testid={`free-block-${b.id}`}>
            <div className="p-1 pointer-events-none"><BlockRenderer block={b} /></div>
            <div className="absolute -top-3 right-1 z-10 hidden group-hover:flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1">
              <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDup(b.id); }} className="p-1 text-slate-500 hover:text-slate-800"><Copy size={12} /></button>
              <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDel(b.id); }} className="p-1 text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>
            </div>
            <div onPointerDown={(e) => onDown(e, b, "resize", idx)} className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-[#E05638]/20 hover:bg-[#E05638]/50" data-testid={`free-resize-${b.id}`} />
          </div>
        );
      })}
    </div>
  );
}

export default function BuilderEditor({ page, onBack, onSaved }) {
  const [meta, setMeta] = useState(() => ({
    title: page.title || "", slug: page.slug || "", meta_description: page.meta_description || "",
    nav_show: !!page.nav_show, nav_label: page.nav_label || "",
    canonical: page.canonical || "", noindex: !!page.noindex,
    route: page.route || "", override_active: !!page.override_active,
  }));
  const [sections, setSections] = useState(() => normalizeSections(page.blocks || []));
  const [pageId, setPageId] = useState(page.id || null);
  const [status, setStatus] = useState(page.status || "draft");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const [pendingChanges, setPendingChanges] = useState(() => {
    if ((page.status || "draft") !== "published" || !page.published) return false;
    const pub = page.published;
    const draftSig = JSON.stringify({ t: page.title || "", m: page.meta_description || "", b: page.blocks || [] });
    const pubSig = JSON.stringify({ t: pub.title || "", m: pub.meta_description || "", b: pub.blocks || [] });
    return draftSig !== pubSig;
  });
  const [showMeta, setShowMeta] = useState(false);
  const [sectionTemplates, setSectionTemplates] = useState([]);
  const [versions, setVersions] = useState(null); // null = panneau fermé, [] = ouvert
  const [aiOpen, setAiOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [seoResult, setSeoResult] = useState(null);
  const [convOpen, setConvOpen] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const saveTimer = useRef(null);
  const inFlight = useRef(null);
  const sigOf = (m, s) => JSON.stringify({ title: m.title, slug: m.slug, meta_description: m.meta_description, nav_show: m.nav_show, nav_label: m.nav_label, canonical: m.canonical, noindex: m.noindex, route: m.route, override_active: m.override_active, blocks: s });
  const lastSaved = useRef(sigOf(meta, sections));
  const latest = useRef({ meta, sections, pageId });
  latest.current = { meta, sections, pageId };

  // ---------- Annuler / Rétablir (historique en mémoire) ----------
  const past = useRef([]); const future = useRef([]); const histLock = useRef(false); const suspendHist = useRef(false); const prevSections = useRef(sections);
  const [histTick, setHistTick] = useState(0);
  useEffect(() => {
    if (histLock.current) { histLock.current = false; prevSections.current = sections; return; }
    if (suspendHist.current) { prevSections.current = sections; return; }
    if (prevSections.current !== sections) {
      past.current.push(prevSections.current);
      if (past.current.length > 60) past.current.shift();
      future.current = [];
      prevSections.current = sections;
      setHistTick((t) => t + 1);
    }
  }, [sections]);
  const undo = () => { if (!past.current.length) return; future.current.push(sections); const prev = past.current.pop(); histLock.current = true; setSections(prev); setSelected(null); setHistTick((t) => t + 1); };
  const redo = () => { if (!future.current.length) return; past.current.push(sections); const nx = future.current.pop(); histLock.current = true; setSections(nx); setSelected(null); setHistTick((t) => t + 1); };
  // Un seul instantané d'historique par glisser en position libre
  const startFreeInteract = () => { past.current.push(clone(latest.current.sections)); if (past.current.length > 60) past.current.shift(); future.current = []; suspendHist.current = true; setHistTick((t) => t + 1); };
  const endFreeInteract = () => { suspendHist.current = false; prevSections.current = latest.current.sections; };

  // ---------- Persistance (brouillon uniquement) ----------
  const persist = useCallback(async () => {
    if (inFlight.current) { try { await inFlight.current; } catch (e) { void e; } }
    const { meta: m, sections: s, pageId: pid } = latest.current;
    if (!m.title.trim()) return;
    const sig = sigOf(m, s);
    setSaveState("saving");
    const run = (async () => {
      const payload = { title: m.title, slug: m.slug, meta_description: m.meta_description, nav_show: m.nav_show, nav_label: m.nav_label, canonical: m.canonical, noindex: m.noindex, route: m.route, override_active: m.override_active, blocks: s };
      if (!pid) {
        const saved = (await api.post("/admin/cms/pages", payload)).data;
        setPageId(saved.id);
        setMeta((cur) => ({ ...cur, slug: saved.slug }));
      } else {
        await api.put(`/admin/cms/pages/${pid}`, payload);
      }
      lastSaved.current = sig;
    })();
    inFlight.current = run;
    try { await run; setSaveState("saved"); }
    catch (err) { setSaveState("idle"); toast.error(formatApiError(err)); }
    finally { inFlight.current = null; }
  }, []);

  const scheduleSave = useCallback(() => {
    const { meta: m, sections: s } = latest.current;
    if (sigOf(m, s) === lastSaved.current) return;
    setSaveState("idle");
    setPendingChanges(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 1500);
  }, [persist]);

  useEffect(() => { scheduleSave(); }, [meta, sections, scheduleSave]);
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  // Modèles de sections (bibliothèque)
  const loadTemplates = useCallback(() => {
    api.get("/admin/cms/templates?kind=section").then(({ data }) => setSectionTemplates(data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ---------- Structure ----------
  const addSection = (nbCols = 1) => { const sec = newSection(nbCols); setSections((arr) => [...arr, sec]); setSelected({ kind: "section", id: sec.id }); };
  const removeSection = (sid) => setSections((arr) => arr.filter((s) => s.id !== sid));
  const duplicateSection = (sid) => setSections((arr) => {
    const i = arr.findIndex((s) => s.id === sid); if (i === -1) return arr;
    const next = [...arr]; next.splice(i + 1, 0, reidSection(clone(arr[i]))); return next;
  });
  const moveSection = (sid, dir) => setSections((arr) => {
    const i = arr.findIndex((s) => s.id === sid); const j = i + dir;
    if (i === -1 || j < 0 || j >= arr.length) return arr;
    const next = [...arr]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const setSectionData = (sid, patch) => setSections((arr) => arr.map((s) => s.id === sid ? { ...s, data: { ...s.data, ...patch } } : s));
  const setSectionColumns = (sid, n) => setSections((arr) => arr.map((s) => {
    if (s.id !== sid) return s;
    const cur = s.columns;
    if (n > cur.length) return { ...s, columns: [...cur, ...Array.from({ length: n - cur.length }, () => newColumn())] };
    if (n < cur.length) {
      const keep = cur.slice(0, n); const merged = cur.slice(n).flatMap((c) => c.blocks);
      keep[n - 1] = { ...keep[n - 1], blocks: [...keep[n - 1].blocks, ...merged] };
      return { ...s, columns: keep };
    }
    return s;
  }));

  const addBlock = (type) => {
    setSections((arr) => {
      if (arr.length === 0) {
        const sec = newSection(1); sec.columns[0].blocks.push(newLeaf(type));
        setSelected({ kind: "block", id: sec.columns[0].blocks[0].id });
        return [sec];
      }
      let target = null;
      if (selected?.kind === "block") { const loc = findBlock(arr, selected.id); if (loc) target = { sid: loc.sid, cid: loc.cid }; }
      else if (selected?.kind === "section") { const s = arr.find((x) => x.id === selected.id); if (s) target = { sid: s.id, cid: s.columns[0].id }; }
      if (!target) { const s = arr[arr.length - 1]; target = { sid: s.id, cid: s.columns[0].id }; }
      const leaf = newLeaf(type);
      const next = arr.map((s) => s.id !== target.sid ? s : { ...s, columns: s.columns.map((c) => c.id !== target.cid ? c : { ...c, blocks: [...c.blocks, leaf] }) });
      setSelected({ kind: "block", id: leaf.id });
      return next;
    });
  };

  const updateLeafData = (patch) => setSections((arr) => arr.map((s) => ({
    ...s, columns: s.columns.map((c) => ({ ...c, blocks: c.blocks.map((b) => {
      if (b.id !== selected?.id) return b;
      const p = typeof patch === "function" ? patch(b.data || {}) : patch;
      return { ...b, data: { ...(b.data || {}), ...p } };
    }) })),
  })));
  const duplicateBlock = (bid) => setSections((arr) => arr.map((s) => ({
    ...s, columns: s.columns.map((c) => {
      const i = c.blocks.findIndex((b) => b.id === bid); if (i === -1) return c;
      const copy = { ...clone(c.blocks[i]), id: rid() }; const blocks = [...c.blocks]; blocks.splice(i + 1, 0, copy); return { ...c, blocks };
    }),
  })));
  const deleteBlock = (bid) => { setSections((arr) => arr.map((s) => ({ ...s, columns: s.columns.map((c) => ({ ...c, blocks: c.blocks.filter((b) => b.id !== bid) })) }))); setSelected((sel) => (sel?.id === bid ? null : sel)); };
  const patchPos = (id, pos) => setSections((arr) => arr.map((s) => ({ ...s, columns: s.columns.map((c) => ({ ...c, blocks: c.blocks.map((b) => (b.id === id ? { ...b, data: { ...(b.data || {}), pos } } : b)) })) })));
  const applyAi = (secs) => { setSections(normalizeSections(secs)); setSelected(null); };
  const insertPreset = (sec) => { const s = reidSection(clone(sec)); setSections((arr) => [...arr, s]); setSelected({ kind: "section", id: s.id }); };
  // Insère les sections suggérées par l'assistant IA de conversion (brouillon uniquement).
  const insertSections = (secs) => {
    const added = (secs || []).map((sec) => reidSection(clone(sec)));
    if (!added.length) return;
    setSections((arr) => [...arr, ...added]);
    setSelected(null);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = clone(latest.current.sections);
    const from = findBlock(next, active.id); if (!from) return;
    const col = next.find((s) => s.id === from.sid).columns.find((c) => c.id === from.cid);
    const [moved] = col.blocks.splice(from.index, 1);
    let dest = null;
    const overSection = next.find((s) => s.columns.some((c) => c.id === over.id));
    if (overSection) { overSection.columns.find((x) => x.id === over.id).blocks.push(moved); dest = true; }
    else { const to = findBlock(next, over.id); if (to) { next.find((s) => s.id === to.sid).columns.find((x) => x.id === to.cid).blocks.splice(to.index, 0, moved); dest = true; } }
    if (dest) setSections(next);
  };

  // ---------- Modèles ----------
  const saveSectionAsTemplate = async (sid) => {
    const sec = sections.find((s) => s.id === sid); if (!sec) return;
    const name = window.prompt("Nom du modèle de section :", sec.data?.name || "Ma section");
    if (!name) return;
    try { await api.post("/admin/cms/templates", { kind: "section", name, data: [sec] }); toast.success("Section enregistrée comme modèle"); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const insertSectionTemplate = (tpl) => {
    const first = (tpl.data || [])[0];
    if (!first) return;
    const sec = reidSection(clone(first));
    setSections((arr) => [...arr, sec]);
    setSelected({ kind: "section", id: sec.id });
    toast.success("Modèle inséré");
  };
  const deleteTemplate = async (tid) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await api.delete(`/admin/cms/templates/${tid}`); loadTemplates(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePageAsTemplate = async () => {
    const name = window.prompt("Nom du modèle de page :", meta.title || "Ma page");
    if (!name) return;
    try { await api.post("/admin/cms/templates", { kind: "page", name, data: sections }); toast.success("Page enregistrée comme modèle"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Versions ----------
  const openVersions = async () => {
    if (!pageId) { toast.error("Publiez la page au moins une fois pour créer un historique."); return; }
    try { const { data } = await api.get(`/admin/cms/pages/${pageId}/versions`); setVersions(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restoreVersion = async (vid) => {
    if (!window.confirm("Restaurer cette version dans le brouillon ? (non publié automatiquement)")) return;
    try {
      const { data } = await api.post(`/admin/cms/pages/${pageId}/restore/${vid}`);
      setSections(normalizeSections(data.blocks || []));
      setMeta((m) => ({ ...m, title: data.title || m.title, meta_description: data.meta_description || m.meta_description }));
      lastSaved.current = sigOf({ ...meta, title: data.title }, normalizeSections(data.blocks || []));
      setPendingChanges(true);
      setVersions(null);
      toast.success("Version restaurée dans le brouillon");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Publication (avec contrôle SEO préalable) ----------
  const doPublish = async () => {
    setSeoResult(null);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persist();
    const pid = latest.current.pageId;
    if (!pid) { toast.error("Enregistrement en cours, réessayez."); return; }
    try {
      await api.post(`/admin/cms/pages/${pid}/publish`);
      setStatus("published"); setPendingChanges(false);
      toast.success("Page publiée ✅ — visible par les visiteurs.");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const publish = async () => {
    if (!meta.title.trim()) { toast.error("Donnez un titre à la page."); return; }
    try {
      const { data } = await api.post("/admin/cms/seo/check", { title: meta.title, meta_description: meta.meta_description, blocks: sections });
      setSeoResult(data);
    } catch (e) { void e; doPublish(); }
  };

  const selectedNode = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === "section") return sections.find((s) => s.id === selected.id) || null;
    const loc = findBlock(sections, selected.id);
    if (!loc) return null;
    return sections.find((s) => s.id === loc.sid).columns.find((c) => c.id === loc.cid).blocks[loc.index];
  }, [selected, sections]);

  const deviceBtn = (key, Icon, label) => (
    <button onClick={() => setDevice(key)} title={label} data-testid={`builder-device-${key}`}
      className={`p-1.5 rounded-md transition ${device === key ? "bg-white shadow text-[#E05638]" : "text-slate-500 hover:text-slate-800"}`}><Icon size={15} /></button>
  );
  const canvasMax = DEVICE_WIDTHS[device];

  return (
    <div data-testid="builder-editor" className="-mx-4 sm:-mx-6 lg:-mx-8">
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-2 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="builder-back"><ArrowLeft size={15} /> Retour</button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Input value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} placeholder="Titre de la page" className="h-9 w-48" data-testid="builder-title" />
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="builder-status">{status === "published" ? "Publiée" : "Brouillon"}</span>
        {status === "published" && pendingChanges && <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700" data-testid="builder-pending">Modifications non publiées</span>}
        <span className="text-xs text-slate-400" data-testid="builder-savestate">{saveState === "saving" ? "Enregistrement…" : saveState === "saved" ? "Brouillon enregistré" : ""}</span>

        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <div className="flex items-center gap-0.5">
            <button onClick={undo} disabled={past.current.length === 0} className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 disabled:opacity-30" title="Annuler" data-testid="builder-undo"><Undo2 size={16} /></button>
            <button onClick={redo} disabled={future.current.length === 0} className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 disabled:opacity-30" title="Rétablir" data-testid="builder-redo"><Redo2 size={16} /></button>
          </div>
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5" data-testid="builder-device">
            {deviceBtn("desktop", Monitor, "Ordinateur")}{deviceBtn("tablet", Tablet, "Tablette")}{deviceBtn("mobile", Smartphone, "Mobile")}
          </div>
          <Button variant="outline" size="sm" onClick={() => setAiOpen(true)} data-testid="builder-ai-btn"><Sparkles size={15} className="mr-1.5" /> IA</Button>
          <Button variant="outline" size="sm" onClick={() => setConvOpen(true)} data-testid="builder-conversion-btn"><TrendingUp size={15} className="mr-1.5" /> Conversion</Button>
          <Button variant="outline" size="sm" onClick={() => setPresetOpen(true)} data-testid="builder-presets-btn"><LayoutTemplate size={15} className="mr-1.5" /> Galerie</Button>
          <Button variant="outline" size="sm" onClick={openVersions} data-testid="builder-versions-btn"><History size={15} className="mr-1.5" /> Versions</Button>
          <Button variant="outline" size="sm" onClick={() => setShowMeta((v) => !v)} data-testid="builder-meta-toggle"><Settings size={15} className="mr-1.5" /> Réglages</Button>
          <Button variant="outline" size="sm" onClick={() => { setPreview((v) => !v); setSelected(null); }} data-testid="builder-preview-toggle">{preview ? <EyeOff size={15} className="mr-1.5" /> : <Eye size={15} className="mr-1.5" />} {preview ? "Éditer" : "Aperçu"}</Button>
          {status === "published" && meta.slug && <a href={`/p/${meta.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-emerald-700 font-semibold hover:underline" data-testid="builder-view-online"><ExternalLink size={14} /> En ligne</a>}
          <Button variant="outline" size="sm" onClick={persist} data-testid="builder-save-draft"><Save size={15} className="mr-1.5" /> Brouillon</Button>
          <Button size="sm" onClick={publish} className="bg-[#E05638] text-white" data-testid="builder-publish"><Rocket size={15} className="mr-1.5" /> Publier</Button>
        </div>
      </div>

      {showMeta && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-4" data-testid="builder-meta-panel">
          <div className="max-w-3xl grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Adresse (slug)</Label>
              <div className="flex items-center gap-1 text-sm"><span className="text-slate-400">/p/</span><Input value={meta.slug} onChange={(e) => setMeta((m) => ({ ...m, slug: e.target.value }))} placeholder="auto depuis le titre" className="h-9" data-testid="builder-slug" /></div>
            </div>
            <div><Label className="text-xs">Description SEO</Label><Textarea rows={2} value={meta.meta_description} onChange={(e) => setMeta((m) => ({ ...m, meta_description: e.target.value }))} placeholder="Résumé Google (~160 car.)" data-testid="builder-meta-desc" /></div>
            <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2"><div><div className="text-sm font-semibold text-slate-800">Afficher dans le menu</div></div><Switch checked={meta.nav_show} onCheckedChange={(v) => setMeta((m) => ({ ...m, nav_show: v }))} data-testid="builder-nav-toggle" /></div>
            {meta.nav_show ? <Input value={meta.nav_label} onChange={(e) => setMeta((m) => ({ ...m, nav_label: e.target.value }))} placeholder="Libellé dans le menu" className="h-9" data-testid="builder-nav-label" /> : <div />}
            <div><Label className="text-xs">URL canonique (SEO, facultatif)</Label><Input value={meta.canonical} onChange={(e) => setMeta((m) => ({ ...m, canonical: e.target.value }))} placeholder="/p/ma-page ou https://…" className="h-9" data-testid="builder-canonical" /></div>
            <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2"><div><div className="text-sm font-semibold text-slate-800">Ne pas indexer (noindex)</div><div className="text-xs text-slate-500">Masquer cette page des moteurs</div></div><Switch checked={meta.noindex} onCheckedChange={(v) => setMeta((m) => ({ ...m, noindex: v }))} data-testid="builder-noindex" /></div>
            {meta.route && (
              <div className="md:col-span-2 flex items-center justify-between rounded-xl bg-orange-50 border border-orange-200 px-3 py-2" data-testid="builder-override-row">
                <div><div className="text-sm font-semibold text-slate-800">Remplacer la page officielle ({meta.route})</div><div className="text-xs text-slate-500">Une fois publiée, cette page s'affichera à la place de l'originale pour tous les visiteurs.</div></div>
                <Switch checked={meta.override_active} onCheckedChange={(v) => setMeta((m) => ({ ...m, override_active: v }))} data-testid="builder-override-toggle" />
              </div>
            )}
            <Button variant="outline" size="sm" className="justify-start" onClick={savePageAsTemplate} data-testid="builder-save-page-template"><LayoutTemplate size={15} className="mr-1.5" /> Enregistrer cette page comme modèle</Button>
          </div>
        </div>
      )}

      {preview ? (
        <div className="bg-white min-h-[70vh] py-4" data-testid="builder-preview">
          <div className={`mx-auto transition-all ${device === "mobile" ? "cms-device-mobile" : ""}`} style={{ maxWidth: canvasMax }}>
            {sections.length === 0 ? <p className="text-center text-slate-400 py-20">Aucun contenu à prévisualiser.</p> : sections.map((s) => <BlockRenderer key={s.id} block={s} />)}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_300px] min-h-[70vh]">
          <aside className="border-r border-slate-200 bg-white p-3 overflow-y-auto" data-testid="builder-library">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Layers size={14} /> Blocs</div>
            {BLOCK_LIBRARY.map((grp) => (
              <div key={grp.group} className="mb-4">
                <div className="text-[11px] font-semibold text-slate-400 mb-1.5">{grp.group}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {grp.items.map((it) => { const Icon = ICONS[it.icon] || Type; return (
                    <button key={it.type} onClick={() => addBlock(it.type)} data-testid={`library-add-${it.type}`} className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:border-[#E05638] hover:text-[#E05638] transition"><Icon size={16} /> {it.label}</button>
                  ); })}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-1" onClick={() => addSection(1)} data-testid="builder-add-section"><Plus size={14} className="mr-1" /> Nouvelle section</Button>

            <div className="mt-5">
              <div className="text-[11px] font-semibold text-slate-400 mb-1.5 flex items-center gap-1"><LayoutTemplate size={12} /> Mes modèles de sections</div>
              {sectionTemplates.length === 0 ? <p className="text-[11px] text-slate-400">Enregistrez une section via l'icône signet pour la réutiliser ici.</p> : (
                <div className="space-y-1.5" data-testid="builder-section-templates">
                  {sectionTemplates.map((t) => (
                    <div key={t.id} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5">
                      <button onClick={() => insertSectionTemplate(t)} className="flex-1 text-left text-xs font-medium text-slate-700 hover:text-[#E05638] truncate" data-testid={`tpl-insert-${t.id}`} title={t.name}>{t.name}</button>
                      <button onClick={() => deleteTemplate(t.id)} className="p-0.5 text-slate-400 hover:text-rose-500" title="Supprimer"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          <main className="bg-slate-100 p-4 overflow-y-auto" onClick={() => setSelected(null)} data-testid="builder-canvas">
            {sections.length === 0 ? (
              <div className="max-w-xl mx-auto mt-16 text-center"><div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10"><Monitor size={32} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-500 mb-4">Page vierge. Ajoutez une section ou un bloc.</p><Button onClick={() => addSection(1)} className="bg-[#E05638] text-white" data-testid="builder-empty-add"><Plus size={15} className="mr-1.5" /> Ajouter une section</Button></div></div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <div className={`mx-auto space-y-4 transition-all ${device === "mobile" ? "cms-device-mobile" : ""}`} style={{ maxWidth: device === "desktop" ? "64rem" : canvasMax }}>
                  {sections.map((s, si) => {
                    const secSel = selected?.kind === "section" && selected.id === s.id;
                    const gridCls = s.columns.length >= 4 ? "md:grid-cols-4" : s.columns.length === 3 ? "md:grid-cols-3" : s.columns.length === 2 ? "md:grid-cols-2" : "grid-cols-1";
                    return (
                      <div key={s.id} onClick={(e) => { e.stopPropagation(); setSelected({ kind: "section", id: s.id }); }} className={`relative rounded-xl border-2 bg-white transition-colors ${secSel ? "border-[#E05638]" : "border-slate-200 hover:border-slate-300"} ${paddPreview[s.data?.paddingY || "md"]} ${styleClasses(s.data)}`} style={s.data?.bg ? { backgroundColor: s.data.bg } : undefined} data-testid={`canvas-section-${si}`}>
                        <div className="absolute -top-3 left-3 z-10 flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 py-0.5 text-slate-500">
                          <span className="px-1.5 text-[10px] font-bold uppercase inline-flex items-center gap-1"><ColumnsIcon size={11} /> Section</span>
                          <button onClick={(e) => { e.stopPropagation(); moveSection(s.id, -1); }} disabled={si === 0} className="p-1 disabled:opacity-30 hover:text-slate-800" title="Monter"><ArrowUp size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); moveSection(s.id, 1); }} disabled={si === sections.length - 1} className="p-1 disabled:opacity-30 hover:text-slate-800" title="Descendre"><ArrowDown size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); saveSectionAsTemplate(s.id); }} className="p-1 hover:text-slate-800" title="Enregistrer comme modèle" data-testid={`canvas-section-tpl-${si}`}><Bookmark size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); duplicateSection(s.id); }} className="p-1 hover:text-slate-800" title="Dupliquer" data-testid={`canvas-section-dup-${si}`}><Copy size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); removeSection(s.id); }} className="p-1 text-rose-500 hover:text-rose-700" title="Supprimer" data-testid={`canvas-section-del-${si}`}><Trash2 size={12} /></button>
                        </div>
                        {s.data?.free ? (
                          <div className="p-3"><FreeSection section={s} selectedId={selected?.kind === "block" ? selected.id : null} onSelectBlock={(id) => setSelected({ kind: "block", id })} patchPos={patchPos} onDup={duplicateBlock} onDel={deleteBlock} onInteractStart={startFreeInteract} onInteractEnd={endFreeInteract} /></div>
                        ) : (
                          <div className={`grid grid-cols-1 ${gridCls} gap-3 p-3`}>
                            {s.columns.map((c) => (
                              <ColumnDrop key={c.id} id={c.id} empty={c.blocks.length === 0}>
                                <SortableContext items={c.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                                  {c.blocks.map((b) => (<SortableLeaf key={b.id} block={b} selected={selected?.kind === "block" && selected.id === b.id} onSelect={() => setSelected({ kind: "block", id: b.id })} onDuplicate={() => duplicateBlock(b.id)} onDelete={() => deleteBlock(b.id)} />))}
                                </SortableContext>
                              </ColumnDrop>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DndContext>
            )}
          </main>

          <aside className="border-l border-slate-200 bg-white p-4 overflow-y-auto" data-testid="builder-properties">
            <PropertiesPanel selected={selectedNode} updateLeafData={updateLeafData} updateSectionData={(patch) => setSectionData(selected.id, patch)} setSectionColumns={(n) => setSectionColumns(selected.id, n)} />
          </aside>
        </div>
      )}

      {versions !== null && (        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setVersions(null)} data-testid="versions-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="font-heading font-bold text-slate-900 flex items-center gap-2"><History size={18} /> Historique des versions</div>
              <button onClick={() => setVersions(null)} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-4">
              {versions.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Aucune version archivée. Chaque publication crée une version restaurable.</p> : (
                <div className="space-y-2">
                  {versions.map((v, i) => (
                    <div key={v.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`version-row-${i}`}>
                      <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{v.title || "Sans titre"}</div><div className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString("fr-FR")}</div></div>
                      <Button variant="outline" size="sm" onClick={() => restoreVersion(v.id)} data-testid={`version-restore-${i}`}>Restaurer</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AiModal open={aiOpen} onClose={() => setAiOpen(false)} currentBlocks={sections} onApply={applyAi} />
      <PresetGallery open={presetOpen} onClose={() => setPresetOpen(false)} onInsert={insertPreset} />
      <SeoChecklist open={seoResult !== null} result={seoResult} onClose={() => setSeoResult(null)} onPublishAnyway={doPublish} />
      <ConversionModal open={convOpen} onClose={() => setConvOpen(false)} title={meta.title} metaDescription={meta.meta_description} currentBlocks={sections} onInsertSections={insertSections} />
    </div>
  );
}
